/**
 * Headless presence wiring for native shells. AppSidebar normally owns the
 * presence broadcast and the native set-status listener, but a shell that draws
 * its OWN sidebar suppresses the web one (see AppLayout's
 * shouldSuppressWebSidebarForNativeShell), so AppSidebar never mounts there and
 * presence would never reach the native account menu. This component runs the
 * same wiring and renders nothing; its effects are inert wherever AppSidebar
 * does mount, so the browser keeps a single owner (AppSidebar) and a
 * sidebar-drawing shell keeps a single owner (this bridge).
 *
 * Gated on hasNativeSidebar, NOT on the platform name: that is the same signal
 * AppLayout uses to drop the web sidebar, so the two cannot disagree about who
 * owns presence. Gating on macOS instead left the iPad (which also draws a
 * native sidebar) with presenceStatus permanently null, so its account footer
 * silently fell back to the workspace name and its status picker could not
 * write back.
 */
import { useCallback, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import { useCall } from '../../features/calls/CallContext';
import { usePresence } from '../../hooks/usePresence';
import { NativeBridge } from '../../services/NativeBridge';
import type { NativePresenceDisplayId } from '../../types/native';
import type { PresenceStatus, ResolvedPresenceStatus } from '../../types/presence';

const PRESENCE_TO_NATIVE: Record<ResolvedPresenceStatus, NativePresenceDisplayId> = {
  available:     'available',
  busy:          'busy',
  dnd:           'dnd',
  be_right_back: 'brb',
  away:          'away',
  offline:       'offline',
  in_meeting:    'in_meeting',
  on_site:       'on_site',
};

export function NativePresenceBridge({ user }: { user: any }) {
  const hasNativeSidebar = NativeBridge.hasNativeSidebar();
  const { updateUser, getUser } = useData();
  const dsUser = user?.id ? getUser(user.id) : null;
  const { status: callStatus, availability: callAvailability } = useCall();

  // Mirrors AppSidebar: prefer the fully-hydrated DataService user for
  // presence fields, falling back to the auth user while it loads.
  const effectivePreferences = (dsUser as any)?.preferences ?? user?.preferences;
  const workingSchedule =
    effectivePreferences?.workingSchedule ??
    (dsUser as any)?.working_schedule ??
    (user as any)?.working_schedule ??
    null;

  const presence = usePresence({
    userId: user?.id,
    profileTimeZone: (dsUser as any)?.timezone ?? (user as any)?.timezone ?? null,
    serverManualEntry: effectivePreferences?.manualPresence ?? undefined,
    callStatus,
    callAvailability,
    workingSchedule,
    onPersist: useCallback(
      (entry) => {
        if (!user?.id) return;
        void updateUser(user.id, {
          preferences: {
            ...(effectivePreferences ?? {}),
            manualPresence: entry,
          },
        } as any);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [user?.id],
    ),
  });

  // Broadcast the resolved status so AuthenticatedController folds it into
  // sidebarState for the native account menu and footer.
  useEffect(() => {
    if (!hasNativeSidebar) return;
    const nativeId = PRESENCE_TO_NATIVE[presence.status];
    window.dispatchEvent(
      new CustomEvent('quantis:presence:status-change', { detail: { status: nativeId } }),
    );
  }, [hasNativeSidebar, presence.status]);

  // Apply set-status requests coming from the native account menu.
  useEffect(() => {
    if (!hasNativeSidebar) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ status: PresenceStatus }>).detail;
      if (detail?.status) {
        presence.setManualStatus(detail.status);
      }
    };
    window.addEventListener('quantis:presence:set-status', handler);
    return () => window.removeEventListener('quantis:presence:set-status', handler);
  // presence.setManualStatus is stable; mirrors the AppSidebar listener.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNativeSidebar]);

  return null;
}
